
import {finalize,  debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { NgForm } from '@angular/forms';
import { Subscription ,  Subject } from 'rxjs';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { BannerNotificationsService, PaginatedList, SpinnerService } from '../../../xform-compat';
import { CompanyBasic, Product } from '../../../shared/models';
import { ProductService } from '../../../core/data-services';
import { EventsHubService } from '../../../core/events-hub.service';
import { CompanyService } from '../../../core/data-services/company';


/**
 * Component class for Add Product Modal Form
 */
@Component({
    selector: 'rms-product-form',
    templateUrl: './product-form.component.html',
    standalone: false
})
export class ProductFormComponent implements OnInit, OnDestroy {

  private modalSubscription: Subscription;
  private modalReference: NgbModalRef;
  @ViewChild('addProductModal', { static: true }) modal: ElementRef;
  public isEdit: boolean;
  public companyList: PaginatedList<CompanyBasic>;
  public synonymsList: Array<{text: string}>;
  public product: Product;
  public name: string;
  public synonyms: Array<string>;
  public companyIds: Array<number>;
  public companiesTypeahead = new Subject<string>();

  constructor(
    private productService: ProductService,
    private companyService: CompanyService,
    private spinnerService: SpinnerService,
    private bannerNotificationService: BannerNotificationsService,
    private translateService: TranslateService,
    private eventsHubService: EventsHubService,
    private modalService: NgbModal,
    private router: Router,
    private cd: ChangeDetectorRef
  ) {
    this.synonymsList = [];
    this.product = new Product();
    this.companyList = new PaginatedList<CompanyBasic>();
  }

  ngOnInit() {
    this.modalSubscription = this.eventsHubService.onAddProductModalOpens$.subscribe(
      // type any because the synonym value to edit has different structure than create
      (product: any) => {
        this.modalReference = this.modalService.open(this.modal, {size: 'lg'});
        this.isEdit = false;
        if (product) {
          this.product = Object.assign({}, product);
          this.isEdit = true;
          this.product.companyIds = product.companies.map(item => item.id);
          this.product.synonyms = product.synonyms.map(item => item.text);
          this.companyList.records = product.companies;
        }
        this.companiesAutocomplete();
        this.modalReference.result.then(
          () => {
            this.product = new Product();
            this.isEdit = false;
          },
          () => {
            this.product = new Product();
            this.isEdit = false;
          }
        );
      }
    );
  }

  ngOnDestroy () {
    this.modalSubscription.unsubscribe();
  }

  /**
   * Typeahead to search companies
   */
  public companiesAutocomplete () {
    this.companiesTypeahead.pipe(
      distinctUntilChanged(),
      debounceTime(200),
      switchMap(term => this.companyService.searchCompanies({ q: term })),
    ).subscribe(value => {
      this.companyList = value;
      this.cd.markForCheck();
    }, () => this.companyList = new PaginatedList<CompanyBasic>());
  }

  /**
   * Process the form, creates a new product and redirects to the new product page
   * @param {NgForm} form
   */
  public processRecord (form: NgForm) {
    if (form.invalid) {
      return;
    }
    this.spinnerService.start();
    if (form.value.synonyms && form.value.synonyms.length) {
      for (let i = 0; i < form.value.synonyms.length; i++) {
        if (typeof form.value.synonyms[i] === 'string') {
          form.value.synonyms[i] = {text: form.value.synonyms[i]};
        }
      }
    }
    if (this.isEdit) {
      this.editProduct(form);
    } else {
      this.createProduct(form);
    }
  }

  /**
   * Creates a new product
   * @param {NgForm} form
   */
  private createProduct (form: NgForm) {
    this.productService.createProduct(form.value).pipe(
      finalize(() => this.spinnerService.stop()))
      .subscribe(
        newProduct => {
          this.bannerNotificationService.success(this.translateService.instant('MESSAGES.CREATED', {value: 'Product'}));
          form.reset();
          this.modalReference.close();
          this.router.navigate(['/product', newProduct.id]);
        },
        error1 => this.bannerNotificationService.error(error1)
      );
  }

  /**
   * Edits the current product name, companies and synonyms
   * @param {NgForm} form
   */
  private editProduct (form: NgForm) {
    this.productService.updateProduct(this.product.id, form.value).pipe(
      finalize(() => this.spinnerService.stop()))
      .subscribe(
        () => {
          this.eventsHubService.productEdited();
          this.bannerNotificationService.success(this.translateService.instant('MESSAGES.UPDATED', {value: 'Product'}));
          form.reset();
          this.modalReference.close();
        },
        error1 => this.bannerNotificationService.error(error1)
      );
  }

}
